import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCheck, X } from "lucide-react";

export default function ReportsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [review, setReview] = useState(null);
  const [resolution, setResolution] = useState("acknowledged");
  const [note, setNote] = useState("");

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/question-reports${q}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async () => {
    try {
      await api.put(`/admin/question-reports/${review.id}`, { resolution, admin_note: note });
      toast.success("Report reviewed");
      setReview(null); setNote(""); setResolution("acknowledged");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const badge = (s) => ({
    open: "bg-amber-100 text-amber-800",
    acknowledged: "bg-blue-100 text-blue-800",
    fixed: "bg-emerald-100 text-emerald-800",
    rejected: "bg-gray-100 text-gray-700",
  }[s] || "bg-secondary");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="reports-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="fixed">Fixed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card className="border-border/70">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Reported</TableHead>
              <TableHead>Question</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Reporter</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                  <TableCell className="text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="text-sm max-w-md truncate">{r.question_text}</div>
                    <div className="text-xs text-muted-foreground">{r.subject_name}</div>
                  </TableCell>
                  <TableCell className="text-xs capitalize">{r.reason.replace("_", " ")}</TableCell>
                  <TableCell className="text-xs">{r.reporter_email}</TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${badge(r.status)}`}>{r.status}</span></TableCell>
                  <TableCell>
                    {r.status === "open" ? (
                      <Button size="sm" variant="outline" onClick={() => setReview(r)} data-testid={`review-${r.id}`}>Review</Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">{r.admin_note || "—"}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="p-10 text-center text-sm text-muted-foreground">No reports.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!review} onOpenChange={() => setReview(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review report</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-3">
              <div className="rounded border border-border bg-secondary/40 p-3 text-sm">
                <div className="font-semibold">{review.question_text}</div>
                <div className="mt-1 text-xs text-muted-foreground">Reason: {review.reason.replace("_", " ")} · Note: {review.note || "—"}</div>
              </div>
              <div>
                <Select value={resolution} onValueChange={setResolution}>
                  <SelectTrigger data-testid="review-resolution"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="acknowledged">Acknowledged (will review later)</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="rejected">Rejected (no issue)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Admin note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-note" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)}>Cancel</Button>
            <Button onClick={submit} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="review-submit">
              <CheckCheck className="mr-2 h-4 w-4" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
